import { EditCompanyPolicyRequest, ResponseErrorDto } from '@interfaces/models';
import { useEmployeesProfile } from '@pages/employees-profile/hooks';
import { updateEmployeeProfile } from '@api/company-policy';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from '@ui/components';
import { TFunction } from 'i18next';
import { useEffect } from 'react';
import { z } from 'zod';

const employeesProfileFormSchema = (t: TFunction) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(3, { message: t('FORMS.MIN_CHARACTERS', { limit: 3 }) })
      .max(50, { message: t('FORMS.MAX_CHARACTERS', { limit: 50 }) }),
    color: z
      .string()
      .trim()
      .min(1, { message: t('FORMS.REQUIRED') }),
    maxAmount: z
      .number({ coerce: true })
      .min(0, { message: t('FORMS.INVALID_CURRENCY_VALUE', { limit: 0 }) })
      .optional()
      .nullable(),
    validFrom: z.date().optional().nullable(),
    validTo: z.date().optional().nullable(),
  });

type EmployeesProfileFormData = z.infer<ReturnType<typeof employeesProfileFormSchema>>;

const DEFAULT_VALUES: EmployeesProfileFormData = {
  name: '',
  color: '',
  maxAmount: null,
  validFrom: null,
  validTo: null,
};

export const useEditEmployeesProfile = ({
  onSuccess,
  id,
}: {
  onSuccess?: () => void;
  id: string;
}) => {
  const { t } = useTranslation();

  const { employeeProfile } = useEmployeesProfile(id);

  const { mutate, isPending } = useMutation({
    mutationFn: (req: EditCompanyPolicyRequest) => updateEmployeeProfile(id, req),
    onSuccess: () => {
      toast.success(t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.UPDATE_PROFILE_SUCCESS'));
      onSuccess?.();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const form = useForm<EmployeesProfileFormData>({
    resolver: zodResolver(employeesProfileFormSchema(t)),
    defaultValues: DEFAULT_VALUES,
  });

  const updateProfileHandler = (data: EmployeesProfileFormData) => {
    const payload = {
      ...employeeProfile,
      ...data,
      validFrom: data.validFrom ? new Date(data.validFrom).toISOString() : null,
      validTo: data.validTo ? new Date(data.validTo).toISOString() : null,
      maxAmount: data.maxAmount || null,
    } as EditCompanyPolicyRequest;
    mutate(payload);
  };

  const onSubmit = form.handleSubmit(updateProfileHandler);

  useEffect(() => {
    if (employeeProfile)
      form.reset({
        ...employeeProfile,
        validFrom: employeeProfile.validFrom ? new Date(employeeProfile.validFrom) : null,
        validTo: employeeProfile.validTo ? new Date(employeeProfile.validTo) : null,
      });
  }, [employeeProfile, form]);

  return {
    isPending,
    onSubmit,
    form,
    t,
  };
};
